import { HistoryResponse, SSEResponse, ThinkingMode } from '../types';

// 根据运行环境动态设置 API 基础 URL
// 在本地开发环境（localhost）使用完整后端地址，生产环境使用环境变量配置
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
export const API_BASE_URL = isLocalhost
  ? 'http://localhost:3001'
  : (process.env.REACT_APP_API_BASE_URL || 'https://lawhelper-api-production.up.railway.app');



// 获取认证 token
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

// 创建带认证的 fetch 选项
function createAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// SSE连接类
export class ChatSSEService {
  private eventSource: EventSource | null = null;
  private onMessageCallback: (data: SSEResponse) => void = () => { };
  private onErrorCallback: (error: Event) => void = () => { };
  private onCloseCallback: () => void = () => { };

  // 建立SSE连接
  connect(sessionId: string) {
    if (this.eventSource) {
      this.disconnect();
    }

    const url = `${API_BASE_URL}/api/chat/stream?session_id=${sessionId}`;
    this.eventSource = new EventSource(url);

    this.eventSource.onmessage = (event) => {
      try {
        const data: SSEResponse = JSON.parse(event.data);
        this.onMessageCallback(data);
      } catch (error) {
        console.error('解析SSE消息失败:', error);
      }
    };

    this.eventSource.onerror = (event) => {
      console.error('SSE连接错误:', event);
      this.onErrorCallback(event);
    };

    this.eventSource.onopen = () => {
      console.log('SSE连接已建立');
    };
  }

  // 断开SSE连接
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.onCloseCallback();
    }
  }

  // 设置消息回调
  onMessage(callback: (data: SSEResponse) => void) {
    this.onMessageCallback = callback;
  }

  // 设置错误回调
  onError(callback: (error: Event) => void) {
    this.onErrorCallback = callback;
  }

  // 设置关闭回调
  onClose(callback: () => void) {
    this.onCloseCallback = callback;
  }
}

// API服务类
export class ChatApiService {
  // 获取历史消息
  static async getHistory(sessionId: string): Promise<HistoryResponse> {
    const response = await fetch(`${API_BASE_URL}/api/chat/history?session_id=${sessionId}`, {
      headers: createAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // 发送消息
  static async sendMessage(sessionId: string, content: string): Promise<{
    success: boolean;
    message_id: string;
    response?: {
      content: string;
      role: string;
      timestamp: number;
      is_final: boolean;
    };
  }> {
    const response = await fetch(`${API_BASE_URL}/api/chat/send`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: JSON.stringify({
        session_id: sessionId,
        content,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // 创建新会话
  static async createSession(userId?: string): Promise<{ session_id: string }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/chat/session`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // 获取会话列表
  static async getSessions(userId?: string): Promise<{ sessions: Array<{ session_id: string; title: string; created_at: number; updated_at: number }> }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    // Debug: 帮助排查跨浏览器会话加载问题
    console.log('[API] getSessions called:', { userId, hasAuthToken: !!localStorage.getItem('auth_token') });

    const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
      headers,
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // 删除会话
  static async deleteSession(sessionId: string): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/conversations/${sessionId}`, {
      method: 'DELETE',
      headers: createAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // 归档/取消归档会话
  static async archiveSession(sessionId: string, archived: boolean): Promise<{ success: boolean; archived?: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/conversations/${sessionId}/archive`, {
      method: 'PUT',
      headers: createAuthHeaders(),
      body: JSON.stringify({ archived }),
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // 获取归档会话列表
  static async getArchivedSessions(userId?: string): Promise<Array<{ id: string; title: string; createdAt: string; updatedAt: string }>> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/conversations/list/archived`, {
      headers,
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  // AI 生成标题
  static async generateTitle(content: string): Promise<{ success: boolean; title: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/generate-title`, {
        method: 'POST',
        headers: createAuthHeaders(),
        body: JSON.stringify({ content }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      return await response.json();
    } catch {
      // Fallback: 截取前30字符
      return { success: false, title: content.slice(0, 30) + (content.length > 30 ? '...' : '') };
    }
  }

  // 流式发送消息（带超时和错误处理）
  static async sendMessageStream(
    sessionId: string,
    content: string,
    onChunk: (text: string) => void,
    onComplete: (conversationId: string, knowledgeReferences?: KnowledgeReference[]) => void,
    onError: (error: string) => void,
    options?: {
      useKnowledge?: boolean;
      userId?: string;
      thinkingMode?: ThinkingMode;
      enabledDocumentIds?: string[]; // 启用的文档 ID 列表
      enabledCaseFolderIds?: string[]; // 启用的案件文件夹 ID 列表
      onThinking?: (content: string) => void;
      onCitations?: (citations: Array<{ sourceId: string; sourceName: string; text: string; pageNumber?: number; chunkIndex?: number }>) => void;
      onStatus?: (status: 'assessing' | 'searching' | 'analyzing' | 'answering') => void;
      onToolRequest?: (tool: string, args: any) => void; // 回调：收到后端工具调用请求
      onFileDownload?: (event: { fileName: string; base64: string; mimeType: string; saveOptions?: string[] }) => void; // 回调：收到文件下载事件
      onTitleUpdate?: (title: string, conversationId: string) => void; // 回调：收到AI生成的标题
      onWorkflowProgress?: (event: { dagId: string; dagName: string; status: string; content?: string; progress?: number; activeWorkers?: string[] }) => void; // 回调：DAG 工作流进度
      onUIEvent?: (event: { action: string; payload: any }) => void; // 回调：__UI_EVENT__ 自动弹窗预览
      documentContext?: string;
      // 原生多模态附件（图片/音频/视频直传 Gemini）
      attachments?: Array<{
        type: 'inline_data' | 'file_data';
        mimeType: string;
        data?: string;     // base64
        fileUri?: string;  // Google File API URI
        name?: string;
      }>;
      signal?: AbortSignal; // 外部取消信号
      forcedSkillId?: string; // 🧩 Architecture 3.0: Slash Command 绿色通道 - 指定 SubAgent ID
    }
  ): Promise<void> {
    const TIMEOUT_MS = 600000; // 600秒超时（10分钟，配合后端心跳机制）
    const controller = new AbortController();
    let timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    let isCancelled = false; // 标记是否是用户取消

    // 如果外部传入了 signal，监听它的 abort 事件
    if (options?.signal) {
      options.signal.addEventListener('abort', () => {
        isCancelled = true; // 标记为用户取消
        controller.abort();
      });
    }

    let hasCompleted = false;
    let hasError = false;

    // 确保只调用一次回调的辅助函数
    const safeComplete = (id: string, refs?: KnowledgeReference[]) => {
      if (!hasCompleted && !hasError) {
        hasCompleted = true;
        clearTimeout(timeoutId);
        onComplete(id, refs);
      }
    };

    const safeError = (msg: string) => {
      if (!hasCompleted && !hasError) {
        hasError = true;
        clearTimeout(timeoutId);
        onError(msg);
      }
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
        method: 'POST',
        headers: createAuthHeaders(),
        body: JSON.stringify({
          message: content,
          conversationId: sessionId,
          useKnowledge: options?.useKnowledge,
          userId: options?.userId,
          thinkingMode: options?.thinkingMode,
          enabledDocumentIds: options?.enabledDocumentIds, // 传递启用的文档列表
          enabledCaseFolderIds: options?.enabledCaseFolderIds, // 传递启用的案件文件夹列表
          documentContext: options?.documentContext, // 当前文档上下文
          attachments: options?.attachments, // 原生多模态附件
          forcedSkillId: options?.forcedSkillId, // 🧩 Architecture 3.0: 绿色通道
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        safeError(`HTTP错误! 状态: ${response.status}`);
        return;
      }

      if (!response.body) {
        safeError('响应体为空');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ''; // 缓冲区用于处理不完整的数据块

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // 流结束，确保调用 onComplete
            safeComplete(sessionId);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // 保留最后一个可能不完整的行
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6).trim();
                if (!jsonStr) continue;

                const event = JSON.parse(jsonStr);
                console.log('SSE Event:', event); // 调试日志

                switch (event.type) {
                  case 'start':
                    console.log('Stream started:', event.conversationId);
                    break;
                  case 'heartbeat':
                    // 收到心跳，重置超时计时器
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
                    break;
                  case 'status':
                    // 状态更新（搜索中、执行工具、生成中）
                    console.log('Status:', event.status, event.tool || '');
                    if (event.status && options?.onStatus) {
                      // 映射后端状态到前端状态
                      const statusMap: Record<string, 'assessing' | 'searching' | 'analyzing' | 'answering'> = {
                        'understanding': 'assessing',
                        'searching': 'searching',
                        'executing_tool': 'searching',
                        'analyzing': 'analyzing',
                        'generating': 'answering',
                      };
                      const mappedStatus = statusMap[event.status] || 'assessing';
                      options.onStatus(mappedStatus);
                    }
                    break;
                  case 'references':
                    // 引用信息
                    console.log('References:', event.urls);
                    break;
                  case 'content':
                    if (event.text) {
                      onChunk(event.text);
                    }
                    break;
                  case 'thinking':
                    // 思维链事件
                    if (event.content && options?.onThinking) {
                      options.onThinking(event.content);
                    }
                    break;
                  case 'citations':
                    // 知识库引用事件（包含预览文本）
                    if (event.citations && options?.onCitations) {
                      options.onCitations(event.citations);
                    }
                    break;
                  case 'file_download':
                    // 后端生成的文件（Word/Excel），触发浏览器下载
                    console.log('File download received:', event.fileName);
                    if (options?.onFileDownload) {
                      options.onFileDownload({
                        fileName: event.fileName,
                        base64: event.base64,
                        mimeType: event.mimeType,
                        saveOptions: event.saveOptions,
                      });
                    }
                    break;
                  case 'workflow_progress':
                    // DAG 工作流进度事件
                    console.log('Workflow progress:', event.dagName, event.status, event.progress);
                    if (options?.onWorkflowProgress) {
                      options.onWorkflowProgress({
                        dagId: event.dagId,
                        dagName: event.dagName,
                        status: event.status,
                        content: event.content,
                        progress: event.progress,
                        activeWorkers: event.activeWorkers,
                      });
                    }
                    break;
                  case 'tool_request':
                    // 后端请求前端执行工具
                    console.log('Tool Request received:', event.tool, event);
                    if (event.tool && options?.onToolRequest) {
                      options.onToolRequest(event.tool, event.args || { path: event.path });
                    }
                    // 约定：收到 tool_request 后流会自然结束，不调用 safeComplete 以免触发 done 逻辑
                    return;
                  case 'ui_event':
                    // __UI_EVENT__: 自动弹窗预览触发器
                    console.log('UI Event received:', event.action, event.payload);
                    if (options?.onUIEvent && event.action) {
                      options.onUIEvent({ action: event.action, payload: event.payload });
                    }
                    break;
                  case 'done':
                    safeComplete(event.conversationId || sessionId, event.knowledgeReferences);
                    return; // 提前返回，流已完成
                  case 'title_update':
                    // AI 生成的对话标题
                    if (event.title && options?.onTitleUpdate) {
                      options.onTitleUpdate(event.title, event.conversationId || sessionId);
                    }
                    break;
                  case 'error':
                    safeError(event.message || 'Unknown error');
                    return;
                }
              } catch (e) {
                console.warn('JSON parse error:', e, 'Line:', line);
                // 继续处理其他行
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          if (isCancelled) {
            // 用户取消 - 直接完成，不显示错误
            safeComplete(sessionId);
          } else {
            // 真正的超时
            safeError('Request timeout, please try again later');
          }
        } else {
          safeError(error.message || 'Network connection failed');
        }
      } else {
        safeError('Unknown error');
      }
    } finally {
      clearTimeout(timeoutId);
      // 确保回调一定被调用
      if (!hasCompleted && !hasError) {
        safeComplete(sessionId);
      }
    }
  }
}

// 认证 API 服务
export class AuthApiService {
  // 获取 OAuth 授权 URL
  static async getOAuthUrl(provider: 'google' | 'microsoft' | 'wechat'): Promise<{ url: string; state: string }> {
    const redirectUri = `${window.location.origin}/auth/callback/${provider}`;
    const response = await fetch(`${API_BASE_URL}/api/auth/oauth/${provider}?redirect_uri=${encodeURIComponent(redirectUri)}`);
    if (!response.ok) {
      throw new Error('Failed to get OAuth URL');
    }
    return await response.json();
  }

  // OAuth 回调处理
  static async handleOAuthCallback(provider: string, code: string): Promise<AuthResponse> {
    const redirectUri = `${window.location.origin}/auth/callback/${provider}`;
    const response = await fetch(`${API_BASE_URL}/api/auth/oauth/${provider}/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
    return await response.json();
  }

  // 发送 Magic Link
  static async sendMagicLink(email: string, language?: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, language }),
    });
    return await response.json();
  }

  // 验证 Magic Link
  static async verifyMagicLink(email: string, token: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, token }),
    });
    return await response.json();
  }

  // 发送 SMS 验证码
  static async sendSMSOtp(phone: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/auth/sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    });
    return await response.json();
  }

  // 验证 SMS 验证码
  static async verifySMSOtp(phone: string, code: string): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, code }),
    });
    return await response.json();
  }

  // 获取当前会话
  static async getSession(): Promise<{ authenticated: boolean; user?: User; session?: { expiresAt: string } }> {
    const token = getAuthToken();
    if (!token) {
      return { authenticated: false };
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return await response.json();
  }

  // 登出
  static async logout(): Promise<void> {
    const token = getAuthToken();
    if (token) {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    }
    localStorage.removeItem('auth_token');
  }
}

// 隐私 API 服务
export class PrivacyApiService {
  // 获取隐私政策信息
  static async getPolicyInfo(region?: string, language?: string): Promise<PolicyInfo> {
    const params = new URLSearchParams();
    if (region) params.append('region', region);
    if (language) params.append('lang', language);

    const response = await fetch(`${API_BASE_URL}/api/privacy/policy?${params}`);
    return await response.json();
  }

  // 记录同意
  static async recordConsent(type: ConsentType, granted: boolean): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/privacy/consent`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: JSON.stringify({ type, granted }),
    });
    return await response.json();
  }

  // 获取同意状态
  static async getConsentStatus(): Promise<{ consents: Record<string, ConsentStatus> }> {
    const response = await fetch(`${API_BASE_URL}/api/privacy/consent`, {
      headers: createAuthHeaders(),
    });
    return await response.json();
  }

  // 导出用户数据
  static async exportData(): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/api/privacy/data`, {
      headers: createAuthHeaders(),
    });
    return await response.blob();
  }

  // 删除用户数据
  static async deleteData(): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/privacy/data`, {
      method: 'DELETE',
      headers: createAuthHeaders(),
      body: JSON.stringify({ confirm: true }),
    });
    return await response.json();
  }

  // 匿名同意 (Cookie Banner)
  static async recordAnonymousConsent(consents: { type: string; granted: boolean }[]): Promise<{ success: boolean }> {
    const browserId = localStorage.getItem('browser_id') || crypto.randomUUID();
    localStorage.setItem('browser_id', browserId);

    const response = await fetch(`${API_BASE_URL}/api/privacy/consent/anonymous`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consents, browserId }),
    });
    return await response.json();
  }
}

// 类型定义
export interface User {
  id: string;
  email?: string;
  phone?: string;
  name?: string;
  avatar?: string;
  tier?: 'free' | 'pro' | 'max' | 'ultra' | 'enterprise';
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  session?: { token: string; expiresAt: string };
  error?: string;
}

export interface PolicyInfo {
  version: string;
  lastUpdated: string;
  applicableFrameworks: string[];
  dataController: { name: string; contact: string };
  userRights: Record<string, boolean>;
}

export type ConsentType = 'privacy_policy' | 'cookie' | 'marketing' | 'data_processing';

export interface ConsentStatus {
  granted: boolean;
  version: string;
  grantedAt: string;
}

// 知识库引用类型
export interface KnowledgeReference {
  sourceId: string;
  sourceName: string;
  text: string;
  pageNumber?: number;
  chunkIndex?: number;
}

// ==================== 文件上传 API 服务 ====================
export class FileApiService {
  // 解析文档提取文本
  static async parseDocument(file: File): Promise<{
    success: boolean;
    text: string;
    metadata?: Record<string, any>;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/files/parse`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: formData,
    });
    return await response.json();
  }

  // 获取支持的文件类型
  static async getSupportedTypes(): Promise<{
    imageTypes: string[];
    documentTypes: string[];
  }> {
    const response = await fetch(`${API_BASE_URL}/api/files/types`);
    return await response.json();
  }

  // ToT File Analysis - 使用 Tree of Thoughts 分析文件树
  static async analyzeFileTree(data: {
    fileTree: string;
    fileContents?: { fileId: string; name: string; kind: string; origin: string; content?: string }[];
    query?: string;
    language?: string;
  }): Promise<{
    success: boolean;
    phase: string;
    files: { fileId: string; relevance: 'sure' | 'likely' | 'impossible'; summary: string }[];
    synthesis: string;
    model: string;
    error?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/files/analyze-tree`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return await response.json();
  }
}

// ==================== 文档生成 API 服务 ====================
export class DocumentApiService {
  // 生成法律文书 PDF
  static async generateLegalDocument(data: {
    type: 'contract' | 'agreement' | 'notice' | 'letter';
    title: string;
    parties?: string[];
    content: string;
    date?: string;
    signatures?: { name: string; title?: string }[];
  }): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/api/documents/generate/legal`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to generate document');
    }
    return await response.blob();
  }

  // 从 DOCX 模板生成
  static async generateFromTemplate(
    templateFile: File,
    data: Record<string, string>,
    outputName?: string
  ): Promise<Blob> {
    const formData = new FormData();
    formData.append('template', templateFile);
    formData.append('data', JSON.stringify(data));
    if (outputName) formData.append('outputName', outputName);

    const response = await fetch(`${API_BASE_URL}/api/documents/generate/docx`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to generate document');
    }
    return await response.blob();
  }

  // 获取 DOCX 模板变量
  static async getTemplateVariables(templateFile: File): Promise<{
    success: boolean;
    variables: string[];
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('template', templateFile);

    const response = await fetch(`${API_BASE_URL}/api/documents/template/variables`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: formData,
    });
    return await response.json();
  }

  // 填充 PDF 表单
  static async fillPdfForm(
    pdfFile: File,
    formData: Record<string, string>,
    outputName?: string
  ): Promise<Blob> {
    const form = new FormData();
    form.append('pdf', pdfFile);
    form.append('formData', JSON.stringify(formData));
    if (outputName) form.append('outputName', outputName);

    const response = await fetch(`${API_BASE_URL}/api/documents/fill/pdf`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: form,
    });

    if (!response.ok) {
      throw new Error('Failed to fill PDF form');
    }
    return await response.blob();
  }

  // 获取 PDF 表单字段
  static async getPdfFormFields(pdfFile: File): Promise<{
    success: boolean;
    fields: { name: string; type: string; value?: string }[];
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('pdf', pdfFile);

    const response = await fetch(`${API_BASE_URL}/api/documents/form/fields`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: formData,
    });
    return await response.json();
  }

  // 审查法律文档
  static async reviewDocument(
    file: File,
    options: {
      country?: string;
      type?: 'contract' | 'agreement' | 'notice' | 'letter' | 'general';
      focusAreas?: string[];
      searchLegalDb?: boolean;
    } = {}
  ): Promise<{
    success: boolean;
    documentId: string;
    summary: string;
    overallRiskLevel: 'low' | 'medium' | 'high' | 'critical';
    score: number;
    issues: any[];
    clauses: any[];
    legalReferences: any[];
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.country) formData.append('country', options.country);
    if (options.type) formData.append('type', options.type);
    if (options.focusAreas) formData.append('focusAreas', JSON.stringify(options.focusAreas));
    if (options.searchLegalDb !== undefined) formData.append('searchLegalDb', String(options.searchLegalDb));

    const response = await fetch(`${API_BASE_URL}/api/documents/review`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: formData,
    });
    return await response.json();
  }

  // 流式审查法律文档
  static async reviewDocumentStream(
    file: File,
    options: {
      country?: string;
      type?: 'contract' | 'agreement' | 'notice' | 'letter' | 'general';
      focusAreas?: string[];
      searchLegalDb?: boolean;
    } = {}
  ): Promise<Response> {
    const formData = new FormData();
    formData.append('file', file);
    if (options.country) formData.append('country', options.country);
    if (options.type) formData.append('type', options.type);
    if (options.focusAreas) formData.append('focusAreas', JSON.stringify(options.focusAreas));
    if (options.searchLegalDb !== undefined) formData.append('searchLegalDb', String(options.searchLegalDb));

    return await fetch(`${API_BASE_URL}/api/documents/review/stream`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: formData,
    });
  }
}

// ==================== 知识库 API 服务 ====================
export class KnowledgeApiService {
  // 上传文档到知识库
  static async uploadDocument(file: File, userId?: string): Promise<{
    success: boolean;
    documentId: string;
    chunkCount: number;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    // Don't include Content-Type header - browser sets multipart/form-data with boundary automatically
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/knowledge/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return await response.json();
  }

  // 获取文档列表
  static async getDocuments(userId?: string): Promise<{
    success: boolean;
    documents: Array<{
      id: string;
      name: string;
      size: number;
      uploadedAt: number;
      chunkCount: number;
    }>;
  }> {
    const headers: Record<string, string> = { ...createAuthHeaders() };
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/knowledge/documents`, {
      headers,
    });
    return await response.json();
  }

  // 删除文档
  static async deleteDocument(documentId: string, userId?: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    const headers: Record<string, string> = { ...createAuthHeaders() };
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/knowledge/documents/${documentId}`, {
      method: 'DELETE',
      headers,
    });
    return await response.json();
  }

  // 语义搜索
  static async search(query: string, topK: number = 5, userId?: string): Promise<{
    success: boolean;
    results: Array<{
      content: string;
      sourceName: string;
      sourceId: string;
      relevance: number;
    }>;
  }> {
    const headers: Record<string, string> = {
      ...createAuthHeaders(),
      'Content-Type': 'application/json',
    };
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/knowledge/search`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, topK }),
    });
    return await response.json();
  }

  // RAG 问答
  static async ask(question: string, topK: number = 5, userId?: string): Promise<{
    success: boolean;
    answer: string;
    citations: Array<{
      sourceId: string;
      sourceName: string;
      text: string;
      relevance: number;
    }>;
    model: string;
  }> {
    const headers: Record<string, string> = {
      ...createAuthHeaders(),
      'Content-Type': 'application/json',
    };
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/knowledge/ask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ question, topK }),
    });
    return await response.json();
  }

  // 搜索法律知识库
  static async searchLegal(query: string, topK: number = 5): Promise<{
    success: boolean;
    results: Array<{
      content: string;
      sourceName: string;
      relevance: number;
    }>;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/knowledge/search-legal`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, topK }),
    });
    return await response.json();
  }

  // 法律知识问答
  static async askLegal(question: string, topK: number = 5): Promise<{
    success: boolean;
    answer: string;
    citations: Array<{
      sourceId: string;
      sourceName: string;
      text: string;
      relevance: number;
    }>;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/knowledge/ask-legal`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question, topK }),
    });
    return await response.json();
  }

  // 获取知识库统计
  static async getStats(userId?: string): Promise<{
    success: boolean;
    stats: {
      documentCount: number;
      chunkCount: number;
      totalSize: number;
    };
  }> {
    const headers: Record<string, string> = { ...createAuthHeaders() };
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/knowledge/stats`, {
      headers,
    });
    return await response.json();
  }

  // 下载 RAG 文档原始文件（从 R2 获取 Blob）
  static async downloadDocument(documentId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/api/knowledge/documents/${documentId}/download`, {
      headers: createAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.blob();
  }
}

// ==================== 法律文书起草 API 服务 ====================
export class DraftingApiService {
  // 获取模板列表
  static async getTemplates(country?: string, category?: string): Promise<{
    success: boolean;
    templates: {
      id: string;
      name: string;
      category: string;
      description: string;
      country: string;
      language: string;
    }[];
  }> {
    const params = new URLSearchParams();
    if (country) params.append('country', country);
    if (category) params.append('category', category);

    const response = await fetch(`${API_BASE_URL}/api/drafting/templates?${params}`, {
      headers: createAuthHeaders(),
    });
    return await response.json();
  }

  // 获取模板详情
  static async getTemplateDetail(templateId: string): Promise<{
    success: boolean;
    template: any;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/drafting/templates/${templateId}`, {
      headers: createAuthHeaders(),
    });
    return await response.json();
  }

  // 搜索在线模板
  static async searchTemplates(
    country: string,
    documentType: string,
    keywords?: string
  ): Promise<{
    success: boolean;
    templates: any[];
    sources: { name: string; url: string }[];
  }> {
    const params = new URLSearchParams({
      country,
      type: documentType,
    });
    if (keywords) params.append('keywords', keywords);

    const response = await fetch(`${API_BASE_URL}/api/drafting/search?${params}`, {
      headers: createAuthHeaders(),
    });
    return await response.json();
  }

  // 起草法律文书
  static async draftDocument(request: {
    documentType: string;
    country: string;
    language?: string;
    parties: {
      role: string;
      name: string;
      type: 'individual' | 'company' | 'organization';
      address?: string;
      representative?: string;
    }[];
    subject: string;
    keyTerms?: { name: string; value: string }[];
    customRequirements?: string;
    useTemplate?: string;
    searchTemplates?: boolean;
  }): Promise<{
    success: boolean;
    documentId: string;
    title: string;
    content: string;
    error?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/drafting/draft`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    return await response.json();
  }

  // 流式起草法律文书
  static async draftDocumentStream(request: {
    documentType: string;
    country: string;
    language?: string;
    parties: any[];
    subject: string;
    keyTerms?: any[];
    customRequirements?: string;
    useTemplate?: string;
    searchTemplates?: boolean;
  }): Promise<Response> {
    return await fetch(`${API_BASE_URL}/api/drafting/draft/stream`, {
      method: 'POST',
      headers: {
        ...createAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }
}

// ==================== 批量请求 API 服务 ====================
// NOTE: OcrApiService 已移除，文件解析统一走 FileApiService.parseDocument
// 对话统一走 ChatApiService.sendMessageStream + documentContext

/**
 * 批量/并发请求服务
 * 
 * 基于 xAI API 异步请求指南实现的前端批量请求能力：
 * - 使用信号量控制最大并发数
 * - 支持进度回调
 * - 显著减少多请求的总执行时间
 */
export class BatchApiService {
  /**
   * 信号量 - 控制并发数
   */
  private static createSemaphore(maxConcurrent: number) {
    let activeCount = 0;
    const queue: (() => void)[] = [];

    return {
      acquire: (): Promise<void> => {
        return new Promise((resolve) => {
          if (activeCount < maxConcurrent) {
            activeCount++;
            resolve();
          } else {
            queue.push(resolve);
          }
        });
      },
      release: (): void => {
        activeCount--;
        if (queue.length > 0) {
          activeCount++;
          const next = queue.shift();
          next?.();
        }
      },
    };
  }

  /**
   * 批量发送聊天消息
   * 
   * @param requests - 消息请求数组
   * @param options - 并发选项
   * @returns 响应结果数组（保持顺序）
   * 
   * @example
   * ```typescript
   * const results = await BatchApiService.batchChat([
   *   { sessionId: 'sess1', content: 'Question 1' },
   *   { sessionId: 'sess2', content: 'Question 2' },
   * ], { maxConcurrent: 3 });
   * ```
   */
  static async batchChat(
    requests: Array<{ sessionId: string; content: string }>,
    options: {
      maxConcurrent?: number;
      timeout?: number;
      onProgress?: (completed: number, total: number, result?: BatchChatResult) => void;
    } = {}
  ): Promise<BatchChatResult[]> {
    const { maxConcurrent = 2, timeout = 60000, onProgress } = options;
    const semaphore = this.createSemaphore(maxConcurrent);
    const results: BatchChatResult[] = new Array(requests.length);
    let completed = 0;

    const processRequest = async (
      request: { sessionId: string; content: string },
      index: number
    ): Promise<void> => {
      await semaphore.acquire();

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
          const response = await fetch(`${API_BASE_URL}/api/chat/send`, {
            method: 'POST',
            headers: createAuthHeaders(),
            body: JSON.stringify({
              session_id: request.sessionId,
              content: request.content,
            }),
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
          }

          const data = await response.json();
          results[index] = {
            success: true,
            index,
            sessionId: request.sessionId,
            response: data.response?.content || '',
          };
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error(`Request ${index + 1} timed out`);
          }
          throw error;
        }
      } catch (error) {
        results[index] = {
          success: false,
          index,
          sessionId: request.sessionId,
          error: error instanceof Error ? error.message : String(error),
        };
      } finally {
        semaphore.release();
        completed++;
        onProgress?.(completed, requests.length, results[index]);
      }
    };

    await Promise.all(requests.map((req, index) => processRequest(req, index)));
    return results;
  }

  /**
   * 批量搜索知识库
   */
  static async batchKnowledgeSearch(
    queries: string[],
    options: {
      maxConcurrent?: number;
      topK?: number;
      userId?: string;
      onProgress?: (completed: number, total: number) => void;
    } = {}
  ): Promise<Array<{ success: boolean; query: string; results?: any[]; error?: string }>> {
    const { maxConcurrent = 3, topK = 5, userId, onProgress } = options;
    const semaphore = this.createSemaphore(maxConcurrent);
    const results: Array<{ success: boolean; query: string; results?: any[]; error?: string }> = [];
    let completed = 0;

    const processQuery = async (query: string, index: number): Promise<void> => {
      await semaphore.acquire();

      try {
        const searchResult = await KnowledgeApiService.search(query, topK, userId);
        results[index] = {
          success: searchResult.success,
          query,
          results: searchResult.results,
        };
      } catch (error) {
        results[index] = {
          success: false,
          query,
          error: error instanceof Error ? error.message : String(error),
        };
      } finally {
        semaphore.release();
        completed++;
        onProgress?.(completed, queries.length);
      }
    };

    await Promise.all(queries.map((query, index) => processQuery(query, index)));
    return results;
  }

  /**
   * 批量上传文件到知识库
   */
  static async batchUploadDocuments(
    files: File[],
    options: {
      maxConcurrent?: number;
      userId?: string;
      onProgress?: (completed: number, total: number, result?: { fileName: string; success: boolean }) => void;
    } = {}
  ): Promise<Array<{ success: boolean; fileName: string; documentId?: string; error?: string }>> {
    const { maxConcurrent = 2, userId, onProgress } = options;
    const semaphore = this.createSemaphore(maxConcurrent);
    const results: Array<{ success: boolean; fileName: string; documentId?: string; error?: string }> = [];
    let completed = 0;

    const processFile = async (file: File, index: number): Promise<void> => {
      await semaphore.acquire();

      try {
        const uploadResult = await KnowledgeApiService.uploadDocument(file, userId);
        results[index] = {
          success: uploadResult.success,
          fileName: file.name,
          documentId: uploadResult.documentId,
        };
      } catch (error) {
        results[index] = {
          success: false,
          fileName: file.name,
          error: error instanceof Error ? error.message : String(error),
        };
      } finally {
        semaphore.release();
        completed++;
        onProgress?.(completed, files.length, { fileName: file.name, success: results[index].success });
      }
    };

    await Promise.all(files.map((file, index) => processFile(file, index)));
    return results;
  }
}

// 批量聊天结果类型
export interface BatchChatResult {
  success: boolean;
  index: number;
  sessionId: string;
  response?: string;
  error?: string;
}

// 工具类型
export type ToolType = 'review' | 'draft' | 'template' | 'form';

// 工具历史记录类型
export interface ToolHistoryRecord {
  id: string;
  toolType: ToolType;
  title: string;
  summary?: string;
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// 工具历史列表记录（不含完整数据）
export interface ToolHistoryListItem {
  id: string;
  toolType: ToolType;
  title: string;
  summary?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 工具历史 API 服务
 * 用于管理 AI 审查、起草、模板、表单的历史记录
 */
export class ToolHistoryApiService {
  /**
   * 创建工具历史记录
   */
  static async createRecord(params: {
    toolType: ToolType;
    title: string;
    summary?: string;
    inputData?: Record<string, unknown>;
    outputData?: Record<string, unknown>;
  }, userId?: string): Promise<{ success: boolean; record?: { id: string; toolType: ToolType; title: string; createdAt: string } }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/tool-history`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * 获取工具历史记录列表
   * @param toolType 可选，按工具类型筛选
   * @param userId 用户ID
   */
  static async getRecords(toolType?: ToolType, userId?: string): Promise<{
    success: boolean;
    records: ToolHistoryListItem[];
    count: number;
  }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const url = toolType
      ? `${API_BASE_URL}/api/tool-history?type=${toolType}`
      : `${API_BASE_URL}/api/tool-history`;

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * 获取单条记录详情
   */
  static async getRecord(recordId: string, userId?: string): Promise<{
    success: boolean;
    record: ToolHistoryRecord;
  }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/tool-history/${recordId}`, { headers });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * 删除工具历史记录
   */
  static async deleteRecord(recordId: string, userId?: string): Promise<{ success: boolean; message?: string }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/tool-history/${recordId}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * 更新工具历史记录
   */
  static async updateRecord(recordId: string, params: {
    title?: string;
    summary?: string;
    inputData?: Record<string, unknown>;
    outputData?: Record<string, unknown>;
  }, userId?: string): Promise<{ success: boolean; record?: ToolHistoryRecord }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/tool-history/${recordId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * 获取工具历史统计
   */
  static async getStats(userId?: string): Promise<{
    success: boolean;
    stats: {
      total: number;
      byType: {
        review: number;
        draft: number;
        template: number;
        form: number;
      };
    };
  }> {
    const headers = createAuthHeaders();
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/api/tool-history/stats/summary`, { headers });
    if (!response.ok) {
      throw new Error(`HTTP错误! 状态: ${response.status}`);
    }
    return await response.json();
  }
}

// ==================== 案件资料库 API 服务 ====================
export class ResourceApiService {
  // 获取所有案件文件夹
  static async getFolders(): Promise<{
    success: boolean;
    folders: Array<{
      id: string;
      name: string;
      status: string;
      enabled: boolean;
      createdAt: string;
      updatedAt: string;
      files: Array<{
        id: string;
        name: string;
        type: string;
        category: string;
        size: number;
        uploadedAt: string;
      }>;
    }>;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/resources/folders`, {
      headers: createAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  }

  // 创建案件文件夹
  static async createFolder(name: string): Promise<{
    success: boolean;
    folder: any;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/resources/folders`, {
      method: 'POST',
      headers: createAuthHeaders(),
      body: JSON.stringify({ name }),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  }

  // 更新案件文件夹
  static async updateFolder(id: string, data: { name?: string; status?: string; enabled?: boolean }): Promise<{
    success: boolean;
    folder: any;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/resources/folders/${id}`, {
      method: 'PUT',
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  }

  // 删除案件文件夹
  static async deleteFolder(id: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/resources/folders/${id}`, {
      method: 'DELETE',
      headers: createAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  }

  // 上传文件到文件夹
  static async uploadFiles(folderId: string, files: File[], category?: string): Promise<{
    success: boolean;
    files: Array<{
      id: string;
      name: string;
      type: string;
      category: string;
      size: number;
      uploadedAt: string;
    }>;
  }> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file);
    }
    if (category) formData.append('category', category);

    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/api/resources/folders/${folderId}/files`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  }

  // 删除文件
  static async deleteFile(fileId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/resources/files/${fileId}`, {
      method: 'DELETE',
      headers: createAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.json();
  }

  // 同步本地文件夹 AiIndex 到云端 (Local Folders → Neon DB)
  static async syncLocalAiIndex(aiIndex: { files: Record<string, any> }): Promise<{ success: boolean; synced: number }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/local-sync`, {
        method: 'POST',
        headers: createAuthHeaders(),
        body: JSON.stringify({ files: aiIndex.files }),
      });
      if (!response.ok) {
        console.warn('[LocalSync] Sync failed:', response.status);
        return { success: false, synced: 0 };
      }
      return await response.json();
    } catch (err) {
      console.warn('[LocalSync] Sync error (non-critical):', err);
      return { success: false, synced: 0 };
    }
  }

  // 下载/预览案件文件（从 R2 获取 Blob）
  static async downloadFile(fileId: string): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/api/resources/files/${fileId}/download`, {
      headers,
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.blob();
  }

  // 下载云端文件（GDrive/OneDrive）原始内容（从后端代理获取 Blob）
  static async downloadCloudFile(source: string, fileId: string): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/api/cloud/${source}/download/${fileId}`, {
      headers,
    });
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return await response.blob();
  }
}